import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { shareOrCopyInviteUrl } from "@/lib/invite-url-share";

export interface InviteLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  url: string;
  emailDeliveryFailed: boolean;
}

export function InviteLinkDialog({
  open,
  onOpenChange,
  email,
  url,
  emailDeliveryFailed,
}: InviteLinkDialogProps) {
  const { t } = useTranslation();
  const hasShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function onShareOrCopy() {
    const clipboard =
      typeof navigator !== "undefined" && navigator.clipboard ? navigator.clipboard : undefined;
    const writeText = clipboard
      ? clipboard.writeText.bind(clipboard)
      : async (_s: string) => undefined;
    const result = await shareOrCopyInviteUrl(url, {
      share: hasShare ? navigator.share.bind(navigator) : undefined,
      writeText,
      title: t("admin.users.invite-link-dialog.share-title"),
    });
    toast.success(
      result.via === "share"
        ? t("admin.users.invite-link-dialog.toast.shared")
        : t("admin.users.invite-link-dialog.toast.copied"),
    );
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("admin.users.invite-link-dialog.title")}
    >
      <div className="space-y-3">
        <p className="text-[13px] text-ink-700">
          {t("admin.users.invite-link-dialog.intro-before")}
          <strong>{email}</strong>
          {t("admin.users.invite-link-dialog.intro-after")}
          {emailDeliveryFailed ? (
            <span className="ml-1 text-vroom-red">
              {t("admin.users.invite-link-dialog.delivery-failed")}
            </span>
          ) : (
            <span className="ml-1">{t("admin.users.invite-link-dialog.share-prompt")}</span>
          )}
        </p>
        <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
        <div className="flex justify-end gap-2">
          <Button variant="default" onClick={() => onOpenChange(false)}>
            {t("common.buttons.close.label")}
          </Button>
          <Button variant="primary" onClick={onShareOrCopy}>
            {hasShare
              ? t("admin.users.invite-link-dialog.share.label")
              : t("admin.users.invite-link-dialog.copy.label")}
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
